import { ArrowDownTray, Check } from '@medusajs/icons';
import {
  Button,
  FocusModal,
  Select,
  Skeleton,
  StatusBadge,
  Switch,
  Text,
  toast,
} from '@pcrm/fe/ui-kit';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { PaperPreview } from '../../components/paper-preview';
import { SectionEmpty } from '../../components/section';
import { ApiError } from '../../lib/api';
import {
  COMPANIES_QUERY_KEY,
  type Company,
  companyDisplayName,
  type InvoiceTheme,
} from './companies.api';
import {
  type InvoiceModelCatalog,
  invoiceModelCatalogQueryOptions,
  previewInvoiceModel,
  previewInvoiceModelHtml,
  saveInvoiceModel,
} from './invoice-model.api';

/**
 * Modelul de factură al unei firme: layout, culoare de accent, bife — și **factura alături,
 * care se schimbă în timp ce alegi**.
 *
 * ⚠️ **Ecran mare, nu sertar** (fondatorul, 2026-09-22: *„nu pot avea live preview? de ce
 * trebuie să tot descarc PDF-uri?"*). Un sertar îngust n-avea unde să pună coala A4, deci
 * singura cale de a vedea rezultatul era un PDF descărcat la fiecare bifă. Aici alegerile
 * stau în stânga și hârtia în dreapta.
 *
 * ⚠️ **Previzualizarea e HTML-ul serverului, nu un desen refăcut în React.** E chiar
 * documentul care pleacă la motorul de PDF; un desen paralel ar fi divergat la prima
 * schimbare de stil, iar „previzualizare care arată altfel decât documentul" e defectul de
 * evitat. PDF-ul rămâne un buton, pentru cine vrea să vadă paginarea reală.
 *
 * ⚠️ **Se deschide de pe rândul firmei, nu pe firma activă din sidebar.** Firmele grupului
 * sunt persoane juridice distincte și pot arăta diferit.
 *
 * **Sub factură stă chitanța de probă** (#187), cu linia punctată de tăiere. Nu are model al ei:
 * urmează layout-ul și culoarea facturii, deci se reface odată cu ea, din același răspuns.
 */
export function InvoiceModelModal({
  company,
  onClose,
}: {
  company: Company | null;
  onClose: () => void;
}) {
  const catalog = useQuery(invoiceModelCatalogQueryOptions());

  return (
    <FocusModal open={company !== null} onOpenChange={(next) => !next && onClose()}>
      <FocusModal.Content>
        {catalog.isPending ? (
          <FocusModal.Body className="flex flex-col gap-3 p-6">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-96 w-full" />
          </FocusModal.Body>
        ) : catalog.error ? (
          <FocusModal.Body className="p-6">
            <SectionEmpty tone="error">
              {catalog.error instanceof Error
                ? catalog.error.message
                : 'Modelele de factură nu au putut fi citite.'}
            </SectionEmpty>
          </FocusModal.Body>
        ) : company ? (
          // Cheia forțează remontarea la schimbarea firmei: ecranul e refolosit, iar fără ea
          // ar fi rămas pe el modelul firmei pe care s-a lucrat înainte.
          <InvoiceModelEditor
            key={company.id}
            company={company}
            catalog={catalog.data}
            onClose={onClose}
          />
        ) : null}
      </FocusModal.Content>
    </FocusModal>
  );
}

function InvoiceModelEditor({
  company,
  catalog,
  onClose,
}: {
  company: Company;
  catalog: InvoiceModelCatalog;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [theme, setTheme] = useState<InvoiceTheme>(company.invoiceTheme);
  /** Câte linii are factura de probă. Vezi comentariul de la selector. */
  const [lineCount, setLineCount] = useState('6');

  const preview = useQuery({
    queryKey: ['invoice-model-preview', company.id, theme, lineCount] as const,
    queryFn: () =>
      previewInvoiceModelHtml({ companyId: company.id, theme, lineCount: Number(lineCount) }),
    // Hârtia rămâne pe ecran cât se cere cea nouă: fără asta, fiecare bifă ar fi golit
    // previzualizarea pentru o clipă, ceea ce arată a defect, nu a încărcare.
    placeholderData: keepPreviousData,
  });

  const save = useMutation({
    mutationFn: () => saveInvoiceModel({ companyId: company.id, theme }),
    onSuccess: async () => {
      toast.success('Modelul de factură a fost salvat');
      await queryClient.invalidateQueries({ queryKey: COMPANIES_QUERY_KEY });
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(error instanceof ApiError ? error.message : 'Salvarea a eșuat.');
    },
  });

  const pdf = useMutation({
    mutationFn: () =>
      previewInvoiceModel({ companyId: company.id, theme, lineCount: Number(lineCount) }),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);

      window.open(url, '_blank', 'noopener');
      // Revocarea imediată ar goli fereastra înainte să apuce s-o citească.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
    onError: (error: unknown) => {
      toast.error(error instanceof ApiError ? error.message : 'PDF-ul nu a putut fi produs.');
    },
  });

  const isBusy = save.isPending;

  return (
    <>
      <FocusModal.Header>
        {/*
          Titlul e cerut de dialogul de dedesubt, nu doar de ochi: fără el, cititoarele de
          ecran anunță o fereastră fără nume, iar Radix scoate un avertisment în consolă.
        */}
        <div className="flex w-full items-center justify-between gap-x-2">
          <div className="flex flex-col">
            <FocusModal.Title asChild>
              <Text size="large" weight="plus">
                Model de factură
              </Text>
            </FocusModal.Title>
            <FocusModal.Description asChild>
              <Text size="small" className="text-ui-fg-subtle">
                {companyDisplayName(company)}
              </Text>
            </FocusModal.Description>
          </div>

          <div className="flex items-center gap-x-2">
            <Button variant="secondary" onClick={onClose} disabled={isBusy}>
              Renunță
            </Button>
            <Button onClick={() => save.mutate()} isLoading={save.isPending} disabled={isBusy}>
              Salvează
            </Button>
          </div>
        </div>
      </FocusModal.Header>

      <FocusModal.Body className="flex flex-1 overflow-hidden">
        <div className="flex w-[380px] shrink-0 flex-col gap-y-6 overflow-y-auto border-r border-ui-border-base p-6">
          <section className="flex flex-col gap-y-2">
            <Text size="small" weight="plus">
              Model
            </Text>
            <div className="grid grid-cols-2 gap-3">
              {catalog.layouts.map((layout) => (
                <button
                  key={layout.id}
                  type="button"
                  onClick={() => setTheme((current) => ({ ...current, layout: layout.id }))}
                  aria-pressed={theme.layout === layout.id}
                  className={`flex flex-col gap-y-2 rounded-lg border p-3 text-left transition-colors ${
                    theme.layout === layout.id
                      ? 'border-ui-border-interactive bg-ui-bg-base-hover'
                      : 'border-ui-border-base'
                  }`}
                >
                  <LayoutSketch layout={layout.id} accentHex={accentOf(catalog, theme)} />
                  <Text size="small" weight="plus">
                    {layout.label}
                  </Text>
                </button>
              ))}
            </div>
          </section>

          <section className="flex flex-col gap-y-2">
            <Text size="small" weight="plus">
              Culoare de accent
            </Text>
            <div className="flex flex-wrap gap-2">
              {catalog.accents.map((accent) => (
                <button
                  key={accent.id}
                  type="button"
                  title={accent.label}
                  aria-label={accent.label}
                  aria-pressed={theme.accent === accent.id}
                  onClick={() => setTheme((current) => ({ ...current, accent: accent.id }))}
                  className="flex size-8 items-center justify-center rounded-full"
                  style={{ backgroundColor: accent.hex }}
                >
                  {/*
                    Bifă albă în pătrățel, nu chenar în jurul lui (fondatorul, 2026-09-22):
                    culorile paletei sunt toate închise, deci albul se vede pe fiecare, iar
                    un chenar negru se pierdea printre ele.
                  */}
                  {theme.accent === accent.id ? <Check className="text-white" /> : null}
                </button>
              ))}
            </div>
            <Text size="xsmall" className="text-ui-fg-subtle">
              Paleta e închisă dinadins: pe o factură tipărită alb-negru, o culoare deschisă face
              scrisul de pe bandă ilizibil.
            </Text>
          </section>

          <section className="flex flex-col gap-y-3">
            <Text size="small" weight="plus">
              Ce se tipărește
            </Text>
            {catalog.options.map((option) => (
              <div key={option.id} className="flex items-start justify-between gap-x-3">
                <div className="flex flex-col">
                  <Text size="small">{option.label}</Text>
                  <Text size="xsmall" className="text-ui-fg-subtle">
                    {option.hint}
                  </Text>
                </div>
                <Switch
                  checked={isOn(theme, option.id)}
                  onCheckedChange={(checked) =>
                    setTheme((current) => ({
                      ...current,
                      options: { ...current.options, [option.id]: checked },
                    }))
                  }
                />
              </div>
            ))}
          </section>
        </div>

        <div className="flex min-w-0 flex-1 flex-col bg-ui-bg-subtle">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ui-border-base px-6 py-3">
            <div className="flex items-center gap-x-2">
              {/*
                Proba lungă nu e un moft: capul de tabel repetat și rândurile care nu se rup se
                văd **numai** pe o factură care trece pe a doua pagină — iar în previzualizarea
                de aici, care e o coală continuă, se vede măcar cât de lung devine tabelul.
              */}
              <Select value={lineCount} onValueChange={setLineCount}>
                <Select.Trigger className="w-[150px]" aria-label="Câte linii are proba">
                  <Select.Value />
                </Select.Trigger>
                <Select.Content className="z-50">
                  <Select.Item value="6">6 linii</Select.Item>
                  <Select.Item value="40">40 de linii</Select.Item>
                </Select.Content>
              </Select>

              {preview.isFetching ? (
                <Text size="xsmall" className="text-ui-fg-muted">
                  se randează…
                </Text>
              ) : null}
            </div>

            <Button
              size="small"
              variant="secondary"
              onClick={() => pdf.mutate()}
              isLoading={pdf.isPending}
            >
              <ArrowDownTray />
              Deschide PDF-ul
            </Button>
          </div>

          <div className="flex-1 overflow-auto p-6">
            {preview.error ? (
              <SectionEmpty tone="error">
                {preview.error instanceof Error
                  ? preview.error.message
                  : 'Previzualizarea nu a putut fi randată.'}
              </SectionEmpty>
            ) : (
              <div className="flex flex-col gap-y-6">
                {/*
                  ⚠️ Deasupra colii, nu pe ea (#187): proba nu inventează o cotă, iar un
                  avertisment tipărit pe factură ar fi arătat ca parte din ea.
                */}
                {preview.data?.vatRateProblem ? (
                  <div className="mx-auto flex w-full max-w-[794px] items-start gap-x-2">
                    <StatusBadge color="orange">Atenție</StatusBadge>
                    <Text size="small">{preview.data.vatRateProblem}</Text>
                  </div>
                ) : null}
                <PaperPreview html={preview.data?.html ?? null} />
                <PaperPreview
                  html={preview.data?.receiptHtml ?? null}
                  title="Previzualizarea chitanței"
                  minHeight={RECEIPT_MIN_HEIGHT_PX}
                />
              </div>
            )}
          </div>
        </div>
      </FocusModal.Body>
    </>
  );
}

/** Cât ocupă chitanța de probă pe ecran până se măsoară: aproximativ o treime de A4. */
const RECEIPT_MIN_HEIGHT_PX = 360;

function accentOf(catalog: InvoiceModelCatalog, theme: InvoiceTheme): string {
  return (
    catalog.accents.find((accent) => accent.id === theme.accent)?.hex ??
    catalog.accents[0]?.hex ??
    '#1b365d'
  );
}

/**
 * Starea unei bife venite din catalog.
 *
 * Catalogul dă id-urile ca `string` — `apps/crm` nu poate importa tipurile modulului — deci
 * o bifă necunoscută trebuie tratată ca stinsă, nu ca `undefined`: un `Switch` cu `checked`
 * nedefinit trece necontrolat și își pierde valoarea la prima randare.
 */
function isOn(theme: InvoiceTheme, optionId: string): boolean {
  return (theme.options as Record<string, boolean | undefined>)[optionId] ?? false;
}

/**
 * Schița modelului, în culoarea aleasă.
 *
 * Nu e o previzualizare — aia e alături, în mărime naturală. E doar reperul din buton, cât
 * să se vadă din ce se alege: jumătate logo, jumătate identitate, cu bandă sau cu linie.
 */
function LayoutSketch({ layout, accentHex }: { layout: string; accentHex: string }) {
  const banded = layout === 'band';

  return (
    <div className="flex flex-col gap-1 rounded border border-ui-border-base bg-ui-bg-base p-2">
      <div className="flex items-center gap-1">
        <div className="h-4 w-1/2 rounded-sm bg-ui-bg-component" />
        {banded ? (
          <div className="h-4 w-1/2 rounded-sm" style={{ backgroundColor: accentHex }} />
        ) : (
          <div className="flex w-1/2 flex-col gap-0.5">
            <div className="h-1.5 w-full rounded-sm bg-ui-bg-component" />
            <div className="h-[2px] w-full" style={{ backgroundColor: accentHex }} />
          </div>
        )}
      </div>

      <div className="mt-1 flex flex-col gap-0.5">
        {banded ? (
          <div className="h-2 w-full rounded-sm" style={{ backgroundColor: accentHex }} />
        ) : (
          <div className="h-2 w-full border-y bg-transparent" style={{ borderColor: accentHex }} />
        )}
        <div className="h-1.5 w-full bg-ui-bg-component" />
        <div className="h-1.5 w-full bg-ui-bg-component" />
        <div className="h-1.5 w-2/3 bg-ui-bg-component" />
      </div>
    </div>
  );
}
